// Shared, cached ad-pool fetcher.
// - One in-flight promise per session (multiple components, StrictMode-safe).
// - Retries on transient failures with backoff.
// - Never wipes a pool that was already delivered — subsequent errors are swallowed.
// - Subscribers can also live-refresh via a manual invalidate() call.
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let currentPromise = null;
let cached = null;              // last successful array
let cachedAt = 0;
const TTL_MS = 60 * 1000;       // refresh at most every 60s
const RETRY_DELAYS = [500, 1500, 4000];

function fetchOnce() {
  return axios.get(`${API}/ads`, { timeout: 10000 }).then((r) => r.data || []);
}

async function fetchWithRetry() {
  let lastErr = null;
  for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
    try {
      const data = await fetchOnce();
      return data;
    } catch (e) {
      lastErr = e;
      const delay = RETRY_DELAYS[attempt];
      if (delay == null) break;
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastErr || new Error("ads fetch failed");
}

/** Return the ad pool; uses cached value if fresh, otherwise fetches with retries.
 *  Never rejects — on total failure, resolves with the previous cache (or []).
 */
export function getAdsPool({ force = false } = {}) {
  const now = Date.now();
  if (!force && cached && now - cachedAt < TTL_MS) {
    return Promise.resolve(cached);
  }
  if (currentPromise) return currentPromise;
  currentPromise = fetchWithRetry()
    .then((data) => {
      cached = Array.isArray(data) ? data : [];
      cachedAt = Date.now();
      return cached;
    })
    .catch(() => cached || [])         // never wipe an existing cache
    .finally(() => { currentPromise = null; });
  return currentPromise;
}

/** Manually invalidate the cache (e.g. after admin creates a new ad). */
export function invalidateAdsPool() {
  cached = null;
  cachedAt = 0;
}
